import { z } from 'zod';

import { type ArtifactStatus, DRAFT_ARTIFACT_CHAIN, type DraftArtifactKind } from './artifacts.ts';
import { DomainError } from './errors.ts';

/**
 * The product lifecycle, whole, even though the prototype only exercises
 * draft → complete: the states the prototype skips are the ones a production build
 * fills in, and leaving them out of the enum would make that a migration.
 */
export const STUDY_STATES = [
  'draft',
  'ready',
  'live',
  'analyzing',
  'complete',
  'under_recruited',
  'cancelled',
] as const;
export const studyStateSchema = z.enum(STUDY_STATES);
export type StudyState = z.infer<typeof studyStateSchema>;

/**
 * Every legal move, exhaustively. `ready` returns to `draft` because an edit that
 * unapproves or stales an artifact takes the study out of ready — launch is blocked
 * while anything is stale. Nothing leaves the three terminal states.
 */
export const STUDY_STATE_TRANSITIONS: Record<StudyState, readonly StudyState[]> = {
  draft: ['ready'],
  ready: ['draft', 'live'],
  live: ['analyzing', 'under_recruited', 'cancelled'],
  analyzing: ['complete', 'under_recruited'],
  complete: [],
  under_recruited: [],
  cancelled: [],
};

export const canTransitionStudyState = (from: StudyState, to: StudyState): boolean =>
  STUDY_STATE_TRANSITIONS[from].includes(to);

export const assertStudyStateTransition = (from: StudyState, to: StudyState): void => {
  if (!canTransitionStudyState(from, to)) {
    throw new DomainError('invalid_state_transition', `cannot move a study from ${from} to ${to}`, {
      from,
      to,
    });
  }
};

/**
 * Ready means the Materials are confirmed at the version the study holds for the Study Type it has
 * now, and all three draft artifacts are approved with none waiting on an upstream change — the
 * "proposal on your desk" moment, and the only doorway to launch.
 *
 * The Materials are read beside the chain because they are what everything in it was drafted from:
 * a changed Study Type or an edit to the Materials leaves a study whose documents rest on facts
 * nobody has confirmed, and a study like that must not be one that can be bought. `materialsConfirmed`
 * is `materialsConfirmedCurrent` as the caller read it under the study row lock.
 */
export const assertStudyReadyForLaunch = (
  states: readonly { kind: DraftArtifactKind; status: ArtifactStatus }[],
  materialsConfirmed: boolean,
): void => {
  if (!materialsConfirmed) {
    throw new DomainError('study_not_ready', 'the materials are not confirmed for this study', {
      kind: 'materials',
      status: null,
    });
  }

  const byKind = new Map(states.map((state) => [state.kind, state.status]));

  for (const kind of DRAFT_ARTIFACT_CHAIN) {
    const status = byKind.get(kind);
    if (status !== 'approved') {
      throw new DomainError('study_not_ready', `${kind} is ${status ?? 'missing'}, not approved`, {
        kind,
        status: status ?? null,
      });
    }
  }
};

export interface StudyDeletability {
  state: StudyState;
  launchedAt: Date | null;
  lockedAt: Date | null;
}

/**
 * A study is deletable until money and participants are involved. `launched_at` and
 * `locked_at` are checked alongside the state because they are the columns the
 * permanence claim actually rests on: a study carrying either has real participant
 * data behind it, whatever its state column says.
 */
export const isStudyDeletable = ({ state, launchedAt, lockedAt }: StudyDeletability): boolean =>
  launchedAt === null && lockedAt === null && (state === 'draft' || state === 'ready');

export const assertStudyDeletable = (study: StudyDeletability): void => {
  if (!isStudyDeletable(study)) {
    throw new DomainError('study_not_deletable', 'a launched or completed study is permanent', {
      state: study.state,
      launched_at: study.launchedAt?.toISOString() ?? null,
      locked_at: study.lockedAt?.toISOString() ?? null,
    });
  }
};
